import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import Input from '@shared/ui/Input/Input';
import '../../../index.css';

/**
 * Thin wrapper around the design-system `<Input>` so the search
 * field inside the dynamic-hint popover matches every other inline
 * filter in the table. The legacy MUI `<TextField>` carried its own
 * border / radius / typography tokens that drifted from the rest of
 * the QHS form surfaces.
 *
 * Carries the same `qhs-filter-clear-btn` clear affordance as
 * TextFilter / DateField so the operator can wipe the search term
 * with a single click without having to select-all + delete.
 */
const SearchIcon = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <circle cx='11' cy='11' r='7' />
    <line x1='21' y1='21' x2='16.65' y2='16.65' />
  </svg>
);

const CloseSvg = (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2.5'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <line x1='18' y1='6' x2='6' y2='18' />
    <line x1='6' y1='6' x2='18' y2='18' />
  </svg>
);

const Search = ({ searchValue, setSearchValue }) => {
  const { titles } = useSelector(state => state.i18n);
  const hasValue = !!(searchValue && searchValue.length);
  const inputRef = useRef(null);

  // Focus on the next frame, not through `autoFocus`. The filter opens in
  // a Popover that mounts its content with `visibility: hidden` until it
  // has measured its position, and a hidden input cannot take focus — so
  // `autoFocus` fired, was dropped, and the operator had to click the box
  // before typing. Same fix as InlinePicker.
  useEffect(() => {
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <Input
      size='sm'
      ref={inputRef}
      fullWidth
      placeholder={titles?.search || 'Search'}
      startIcon={<SearchIcon />}
      endIcon={
        hasValue ? (
          <button
            type='button'
            onClick={() => setSearchValue('')}
            aria-label='Clear search'
            className='qhs-filter-clear-btn'
          >
            {CloseSvg}
          </button>
        ) : undefined
      }
      value={searchValue || ''}
      onChange={e => setSearchValue(e.target.value)}
    />
  );
};

export default Search;
